export const BRAND = {
  name: "Floodplains",
  domain: "floodplains.net",
  parent: "OneNetwork",
  sibling: "floodplain.com.au",
  siblingUrl: "https://floodplain.com.au",
  accent: "#2AA6A6",
  mark: "FS",
  tagline: "The living intelligence network for flood models, catchments, evidence, and decisions.",
  hero: "Understand the system.",
  proof: "Flood knowledge should survive the project, the consultant, and the software version.",
  neverClaim:
    "We never present a model as correct, never treat a common historic parameter as a recommended value, and never collapse a model into one quality score.",
};

export const DISCLAIMER =
  "Floodplains organises public and contributed flood-system intelligence. It is not engineering advice, a statutory planning determination, or a model certification. Extracted parameters are not recommended design values. Values used in one catchment are not automatically appropriate for another. Absence of a documented assumption is not evidence that the assumption does not exist. Professional assessment is required before using this material for design, planning, or construction.";

export const ATLAS_WARNING =
  "Values used elsewhere are not automatically appropriate for this catchment or model.";

export const CHAIN = [
  "Catchment",
  "Study",
  "Model",
  "Assumption",
  "Observation",
  "Validation",
  "Decision",
  "Outcome",
  "Learning",
] as const;

export const SYSTEM_TABS = [
  "Overview",
  "Map",
  "Models",
  "Studies",
  "Events",
  "Assumptions",
  "Decisions",
  "People",
  "Evidence",
  "Timeline",
  "Knowledge gaps",
] as const;

export const MODEL_TABS = [
  "Overview",
  "Lineage",
  "Geometry & terrain",
  "Hydrology",
  "Hydraulics",
  "Calibration",
  "Validation",
  "Assumptions",
  "Sensitivity",
  "Decisions",
  "Evidence",
  "Contributors",
] as const;

export const CONCEPTS = [
  {
    slug: "mannings-roughness",
    title: "Manning's roughness",
    dek: "An empirical coefficient representing energy loss from bed and floodplain friction. It is an assumption, not a measured fact.",
  },
  {
    slug: "model-calibration",
    title: "Model calibration",
    dek: "Adjusting a model so simulated levels, extents, or flows reasonably reproduce selected historical events. Fit is not certainty.",
  },
  {
    slug: "validation",
    title: "Validation",
    dek: "Testing a model against an independent event or observation that was not used to set the parameters.",
  },
  {
    slug: "hec-ras",
    title: "HEC-RAS",
    dek: "A one- and two-dimensional hydraulic model widely used for riverine flood studies.",
  },
  {
    slug: "tuflow",
    title: "TUFLOW",
    dek: "A two-dimensional hydrodynamic model commonly used for Australian floodplain and overland-flow studies.",
  },
  {
    slug: "1d-vs-2d",
    title: "1D vs 2D modelling",
    dek: "One-dimensional models represent flow along a channel; two-dimensional models represent flow across a grid or mesh of the floodplain.",
  },
  {
    slug: "flood-frequency-analysis",
    title: "Flood-frequency analysis",
    dek: "Statistical estimation of how often a given discharge or level is expected to be equalled or exceeded.",
  },
  {
    slug: "design-events",
    title: "Design events",
    dek: "Standardised flood magnitudes (for example 1% AEP) used for planning and infrastructure, produced from hydrology and hydraulics rather than a single historical flood.",
  },
  {
    slug: "flood-planning-levels",
    title: "Flood planning levels",
    dek: "Planning control levels, usually a design flood plus freeboard, used to set floors and development controls. They depend on a model, not on the land itself.",
  },
  {
    slug: "terrain-lidar",
    title: "Terrain / LiDAR",
    dek: "The ground surface a hydraulic model sits on. Vintage, resolution, and how structures were burned in all change results.",
  },
  {
    slug: "model-uncertainty",
    title: "Model uncertainty",
    dek: "Calibration fit is not the same as certainty. Geometry, parameters, inputs, model form, and extrapolation all remain.",
  },
  {
    slug: "sensitivity-analysis",
    title: "Sensitivity analysis",
    dek: "Testing how much outputs move when an assumption changes. Sensitivity is not the same as likelihood.",
  },
  {
    slug: "equifinality",
    title: "Equifinality / non-uniqueness",
    dek: "Different parameter sets can reproduce the same calibration event. A good fit does not identify a unique truth.",
  },
] as const;
